import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { UserCog, Trash2, Loader2, Shield, ShieldCheck, Eye, KeyRound, UserPlus, Copy, Globe, Clock, Activity, ArrowUp, ArrowDown, ArrowUpDown, ChevronDown, ChevronUp, Monitor, ListTodo, Briefcase, AlertTriangle, Flag, Wrench, CheckCircle2, Circle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { API } from '../utils/api';

// Priority configuration for tasks
const PRIORITY_CONFIG = {
  High: { color: 'bg-rose-100 text-rose-700 border-rose-200', icon: AlertTriangle },
  Low: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Flag },
  Maintenance: { color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Wrench }
};

// Format date for display (consistent format across app)
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

function Users() {
  const { getAuthHeaders, user: currentUser, login } = useAuth();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [emulateDialogOpen, setEmulateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [usersCollapsed, setUsersCollapsed] = useState(() => {
    return localStorage.getItem('usersSection_collapsed') === 'true';
  });
  const [auditCollapsed, setAuditCollapsed] = useState(() => {
    return localStorage.getItem('auditSection_collapsed') === 'true';
  });
  const [tasksCollapsed, setTasksCollapsed] = useState(() => {
    return localStorage.getItem('tasksSection_collapsed') === 'true';
  });

  // When clicking on Users nav link while already on page, uncollapse System Users
  useEffect(() => {
    if (location.pathname === '/users' && usersCollapsed) {
      setUsersCollapsed(false);
      localStorage.setItem('usersSection_collapsed', 'false');
      // Scroll to top to show the panel
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.key]); // location.key changes on every navigation, even to same path
  const [auditDisplayLimit, setAuditDisplayLimit] = useState(25); // 25, 100, or 'all'
  const [sortColumn, setSortColumn] = useState(() => {
    return localStorage.getItem('usersSortColumn') || 'name';
  });
  const [sortDirection, setSortDirection] = useState(() => {
    return localStorage.getItem('usersSortDirection') || 'asc';
  });
  const [taskSortColumn, setTaskSortColumn] = useState(() => {
    return localStorage.getItem('tasksSortColumn') || 'created_at';
  });
  const [taskSortDirection, setTaskSortDirection] = useState(() => {
    return localStorage.getItem('tasksSortDirection') || 'desc';
  });

  // Handle column sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      const newDir = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDir);
      localStorage.setItem('usersSortDirection', newDir);
    } else {
      setSortColumn(column);
      setSortDirection('asc');
      localStorage.setItem('usersSortColumn', column);
      localStorage.setItem('usersSortDirection', 'asc');
    }
  };

  // Toggle collapse functions
  const toggleUsersSection = () => {
    const newState = !usersCollapsed;
    setUsersCollapsed(newState);
    localStorage.setItem('usersSection_collapsed', newState.toString());
  };

  const toggleAuditSection = () => {
    const newState = !auditCollapsed;
    setAuditCollapsed(newState);
    localStorage.setItem('auditSection_collapsed', newState.toString());
  };

  const toggleTasksSection = () => {
    const newState = !tasksCollapsed;
    setTasksCollapsed(newState);
    localStorage.setItem('tasksSection_collapsed', newState.toString());
  };

  // Parse user agent for display
  const parseUserAgent = (ua) => {
    if (!ua || ua === 'Unknown') return { browser: 'Unknown', os: 'Unknown' };
    
    let browser = 'Unknown';
    let os = 'Unknown';
    
    // Detect browser
    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('MSIE') || ua.includes('Trident')) browser = 'IE';
    
    // Detect OS
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac OS')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    
    return { browser, os };
  };

  // Sorted users
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'email':
          aVal = (a.email || '').toLowerCase();
          bVal = (b.email || '').toLowerCase();
          break;
        case 'role':
          // Sort by role priority: superadmin > manager > professional > viewer
          const roleOrder = { superadmin: 0, manager: 1, professional: 2, viewer: 3 };
          aVal = roleOrder[a.role] ?? 4;
          bVal = roleOrder[b.role] ?? 4;
          break;
        case 'login_count':
          aVal = a.login_count || 0;
          bVal = b.login_count || 0;
          break;
        case 'last_login':
          aVal = a.last_login_at || '';
          bVal = b.last_login_at || '';
          break;
        default:
          aVal = '';
          bVal = '';
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, sortColumn, sortDirection]);

  // Sort icon component
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-40" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  // Task sort icon component
  const TaskSortIcon = ({ column }) => {
    if (taskSortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-40" />;
    }
    return taskSortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  // Handle task column sort
  const handleTaskSort = (column) => {
    if (taskSortColumn === column) {
      const newDir = taskSortDirection === 'asc' ? 'desc' : 'asc';
      setTaskSortDirection(newDir);
      localStorage.setItem('tasksSortDirection', newDir);
    } else {
      setTaskSortColumn(column);
      setTaskSortDirection('asc');
      localStorage.setItem('tasksSortColumn', column);
      localStorage.setItem('tasksSortDirection', 'asc');
    }
  };

  // Sorted tasks
  const sortedTasks = useMemo(() => {
    return [...allTasks].sort((a, b) => {
      let aVal, bVal;
      
      switch (taskSortColumn) {
        case 'priority':
          const priorityOrder = { High: 0, Maintenance: 1, Low: 2 };
          aVal = priorityOrder[a.priority] ?? 3;
          bVal = priorityOrder[b.priority] ?? 3;
          break;
        case 'llc_name':
          aVal = (a.llc_name || '').toLowerCase();
          bVal = (b.llc_name || '').toLowerCase();
          break;
        case 'assigned_to_name':
          aVal = (a.assigned_to_name || '').toLowerCase();
          bVal = (b.assigned_to_name || '').toLowerCase();
          break;
        case 'created_by_name':
          aVal = (a.created_by_name || '').toLowerCase();
          bVal = (b.created_by_name || '').toLowerCase();
          break;
        case 'task_description':
          aVal = (a.task_description || '').toLowerCase();
          bVal = (b.task_description || '').toLowerCase();
          break;
        case 'due_date':
          aVal = a.due_date ? new Date(a.due_date).getTime() : Infinity;
          bVal = b.due_date ? new Date(b.due_date).getTime() : Infinity;
          break;
        case 'created_at':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case 'status':
          aVal = a.date_completed ? 1 : 0;
          bVal = b.date_completed ? 1 : 0;
          break;
        default:
          aVal = a[taskSortColumn] || '';
          bVal = b[taskSortColumn] || '';
      }
      
      if (aVal < bVal) return taskSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return taskSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [allTasks, taskSortColumn, taskSortDirection]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    const headers = getAuthHeaders();
    const isManagerOrAdmin = currentUser?.role === 'superadmin' || currentUser?.role === 'manager';
    
    const requests = [
      axios.get(API + '/users', { headers }),
      axios.get(API + '/investors', { headers }),
      axios.get(API + '/users/audit-logs?limit=1000', { headers })
    ];
    
    // Only fetch all tasks if manager or superadmin
    if (isManagerOrAdmin) {
      requests.push(axios.get(API + '/tasks/all', { headers }).catch(() => ({ data: [] })));
    }
    
    Promise.all(requests).then((responses) => {
      setUsers(responses[0].data);
      setInvestors(responses[1].data);
      setAuditLogs(responses[2].data);
      if (isManagerOrAdmin && responses[3]) {
        setAllTasks(responses[3].data);
      }
      setLoading(false);
    }).catch(() => {
      toast.error('Failed to load users');
      setLoading(false);
    });
  };

  // Get displayed audit logs based on limit
  const displayedAuditLogs = auditDisplayLimit === 'all' 
    ? auditLogs 
    : auditLogs.slice(0, auditDisplayLimit);

  const handleEmulateUser = async () => {
    if (!selectedUser) return;
    setFormLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/emulate/${selectedUser.id}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      // Store original admin credentials for returning
      const originalToken = localStorage.getItem('token');
      const originalUser = localStorage.getItem('user');
      localStorage.setItem('emulation_original_token', originalToken);
      localStorage.setItem('emulation_original_user', originalUser);
      localStorage.setItem('emulating_as', selectedUser.name);
      
      // Log in as the emulated user
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success(`Now viewing as ${selectedUser.name}`);
      setEmulateDialogOpen(false);
      
      // Redirect to dashboard
      window.location.href = '/';
    } catch (error) {
      toast.error('Failed to emulate user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = () => {
    if (!selectedUser) return;
    axios.delete(API + '/users/' + selectedUser.id, { headers: getAuthHeaders() })
      .then(() => {
        toast.success('User deleted');
        setDeleteDialogOpen(false);
        setSelectedUser(null);
        fetchData();
      })
      .catch((error) => {
        toast.error(error.response?.data?.detail || 'Failed');
      });
  };

  const handleRoleChange = (userId, newRole) => {
    axios.put(API + '/users/' + userId + '/role', { role: newRole }, { headers: getAuthHeaders() })
      .then(() => {
        toast.success('Role updated');
        fetchData();
      })
      .catch((error) => {
        toast.error(error.response?.data?.detail || 'Failed');
      });
  };

  const handleTasksToggle = (userId, enabled) => {
    axios.put(API + '/users/' + userId + '/tasks', { tasks_enabled: enabled }, { headers: getAuthHeaders() })
      .then(() => {
        toast.success(enabled ? 'Tasks enabled' : 'Tasks disabled');
        fetchData();
      })
      .catch((error) => {
        toast.error(error.response?.data?.detail || 'Failed to update tasks setting');
      });
  };

  const handleEmailToggle = (userId, enabled) => {
    axios.put(API + '/users/' + userId + '/email', { email_enabled: enabled }, { headers: getAuthHeaders() })
      .then(() => {
        toast.success(enabled ? 'Email enabled' : 'Email disabled');
        fetchData();
      })
      .catch((error) => {
        toast.error(error.response?.data?.detail || 'Failed to update email setting');
      });
  };

  const handleResetPassword = () => {
    if (!selectedUser || !newPassword) return;
    setFormLoading(true);
    axios.put(API + '/users/' + selectedUser.id + '/reset-password', { new_password: newPassword }, { headers: getAuthHeaders() })
      .then(() => {
        toast.success('Password reset');
        setResetDialogOpen(false);
        setSelectedUser(null);
        setNewPassword('');
        setFormLoading(false);
      })
      .catch((error) => {
        toast.error(error.response?.data?.detail || 'Failed');
        setFormLoading(false);
      });
  };

  const handleSyncInvestors = () => {
    setFormLoading(true);
    axios.post(API + '/users/sync-investors', {}, { headers: getAuthHeaders() })
      .then((response) => {
        setSyncResults(response.data);
        toast.success(response.data.message);
        fetchData();
        setFormLoading(false);
      })
      .catch((error) => {
        toast.error(error.response?.data?.detail || 'Failed');
        setFormLoading(false);
      });
  };

  const handleClearAuditLogs = () => {
    if (!window.confirm('Are you sure you want to clear all audit logs? This action cannot be undone.')) {
      return;
    }
    setFormLoading(true);
    axios.delete(API + '/users/audit-logs', { headers: getAuthHeaders() })
      .then((response) => {
        toast.success(response.data.message);
        setAuditLogs([]);
        setFormLoading(false);
      })
      .catch((error) => {
        toast.error(error.response?.data?.detail || 'Failed to clear audit logs');
        setFormLoading(false);
      });
  };

  const copyPassword = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Handle ISO datetime strings (with T) vs date-only strings
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const investorsWithoutAccounts = investors.filter(inv => !users.some(u => u.email === inv.email));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
      </div>
    );
  }

  // Render user rows
  const renderUserRows = () => {
    const rows = [];
    for (let i = 0; i < sortedUsers.length; i++) {
      const user = sortedUsers[i];
      const isCurrentUser = user.id === currentUser?.id;
      const isSuperadmin = user.role === 'superadmin';
      const currentIsSuperadmin = currentUser?.role === 'superadmin';
      const canEmulate = currentIsSuperadmin && !isCurrentUser;
      
      rows.push(
        <TableRow key={user.id} className="table-row-hover">
          <TableCell className="font-medium">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                {user.name.charAt(0).toUpperCase()}
              </div>
              {canEmulate ? (
                <button
                  onClick={() => {
                    setSelectedUser(user);
                    setEmulateDialogOpen(true);
                  }}
                  className="text-left hover:text-amber-700 hover:underline transition-colors"
                  title={`Click to view as ${user.name}`}
                >
                  {user.name}
                </button>
              ) : (
                <span>{user.name}</span>
              )}
              {isCurrentUser && <Badge variant="outline" className="text-xs">You</Badge>}
              {canEmulate && (
                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 cursor-pointer"
                  onClick={() => {
                    setSelectedUser(user);
                    setEmulateDialogOpen(true);
                  }}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Emulate
                </Badge>
              )}
            </div>
          </TableCell>
          <TableCell className="text-slate-600">{user.email}</TableCell>
          <TableCell>
            {isCurrentUser || (isSuperadmin && !currentIsSuperadmin) ? (
              <Badge className={user.role === 'superadmin' ? 'bg-amber-100 text-amber-800' : user.role === 'manager' ? 'bg-blue-100 text-blue-800' : user.role === 'professional' ? 'bg-purple-100 text-purple-800' : ''}>
                {user.role === 'superadmin' && <ShieldCheck className="w-3 h-3 mr-1" />}
                {user.role === 'manager' && <Shield className="w-3 h-3 mr-1" />}
                {user.role === 'professional' && <Briefcase className="w-3 h-3 mr-1" />}
                {user.role === 'viewer' && <Eye className="w-3 h-3 mr-1" />}
                {user.role === 'superadmin' ? 'Super Admin' : user.role === 'manager' ? 'Manager' : user.role === 'professional' ? 'Professional' : 'Viewer'}
              </Badge>
            ) : (
              <Select value={user.role} onValueChange={(v) => handleRoleChange(user.id, v)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  {currentIsSuperadmin && <SelectItem value="superadmin">Super Admin</SelectItem>}
                </SelectContent>
              </Select>
            )}
          </TableCell>
          <TableCell>
            <div className="flex flex-col gap-1">
              {user.last_ip ? (
                <div className="flex items-center gap-1 text-xs">
                  <Globe className="w-3 h-3 text-slate-400" />
                  <span className="font-mono text-slate-600">{user.last_ip}</span>
                </div>
              ) : (
                <span className="text-xs text-slate-400">Never logged in</span>
              )}
              <div className="flex gap-1">
                {user.login_count > 0 && (
                  <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                    <Activity className="w-3 h-3 mr-1" />
                    {user.login_count} logins
                  </Badge>
                )}
                {user.investor_id && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                    Partner
                  </Badge>
                )}
              </div>
            </div>
          </TableCell>
          <TableCell>
            {user.last_login ? (
              <div className="flex items-center gap-1 text-sm text-slate-600">
                <Clock className="w-3 h-3 text-slate-400" />
                {formatDate(user.last_login)}
              </div>
            ) : (
              <span className="text-xs text-slate-400">—</span>
            )}
          </TableCell>
          <TableCell>
            <div className="flex items-center justify-center">
              <Switch
                checked={user.tasks_enabled || false}
                onCheckedChange={(checked) => handleTasksToggle(user.id, checked)}
                disabled={!currentIsSuperadmin}
                data-testid={`tasks-toggle-${user.id}`}
              />
            </div>
          </TableCell>
          <TableCell>
            <div className="flex items-center justify-center">
              <Switch
                checked={user.email_enabled || false}
                onCheckedChange={(checked) => handleEmailToggle(user.id, checked)}
                disabled={!currentIsSuperadmin}
                data-testid={`email-toggle-${user.id}`}
              />
            </div>
          </TableCell>
          <TableCell>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setSelectedUser(user); setResetDialogOpen(true); }}
              disabled={isSuperadmin && !currentIsSuperadmin && !isCurrentUser}
            >
              <KeyRound className="w-4 h-4 mr-1" />Reset
            </Button>
          </TableCell>
          <TableCell>
            {!isCurrentUser && (!isSuperadmin || currentIsSuperadmin) && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-rose-600"
                onClick={() => { setSelectedUser(user); setDeleteDialogOpen(true); }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </TableCell>
        </TableRow>
      );
    }
    return rows;
  };

  // Render investor items
  const renderInvestorItems = () => {
    const items = [];
    for (let i = 0; i < investorsWithoutAccounts.length; i++) {
      const inv = investorsWithoutAccounts[i];
      items.push(
        <div key={inv.id} className="flex items-center gap-2 text-sm p-2 bg-slate-50 rounded">
          <UserPlus className="w-4 h-4 text-slate-400" />
          <span className="font-medium">{inv.name}</span>
          <span className="text-slate-500">({inv.email})</span>
        </div>
      );
    }
    return items;
  };

  // Render created account items
  const renderCreatedItems = () => {
    if (!syncResults || !syncResults.created) return null;
    const items = [];
    for (let i = 0; i < syncResults.created.length; i++) {
      const item = syncResults.created[i];
      items.push(
        <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
          <span>{item.email}</span>
          <div className="flex items-center gap-2">
            <code className="px-2 py-0.5 bg-slate-200 rounded text-xs">{item.default_password}</code>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyPassword(item.default_password)}>
              <Copy className="w-3 h-3" />
            </Button>
          </div>
        </div>
      );
    }
    return items;
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900">User Management</h1>
          <p className="text-slate-500 mt-1">Manage system users and access levels</p>
        </div>
        {investorsWithoutAccounts.length > 0 && (
          <Button onClick={() => setSyncDialogOpen(true)} className="bg-amber-700 hover:bg-amber-800" data-testid="sync-investors-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Add {investorsWithoutAccounts.length} Partner(s) as Users
          </Button>
        )}
      </div>

      <Card className="border-slate-200">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={toggleUsersSection}
        >
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-serif">System Users</CardTitle>
              <CardDescription>{users.length} user(s)</CardDescription>
            </div>
            <Button variant="ghost" size="icon">
              {usersCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
            </Button>
          </div>
        </CardHeader>
        {!usersCollapsed && (
          <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-12">
              <UserCog className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="font-serif text-lg font-semibold text-slate-700">No users</h3>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-800 text-white">
                  <TableHead 
                    className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Name
                      <SortIcon column="name" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center">
                      Email
                      <SortIcon column="email" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors"
                    onClick={() => handleSort('role')}
                  >
                    <div className="flex items-center">
                      User Type
                      <SortIcon column="role" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors"
                    onClick={() => handleSort('login_count')}
                  >
                    <div className="flex items-center">
                      Security
                      <SortIcon column="login_count" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors"
                    onClick={() => handleSort('last_login')}
                  >
                    <div className="flex items-center">
                      Last Login
                      <SortIcon column="last_login" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors"
                    onClick={() => handleSort('tasks_enabled')}
                  >
                    <div className="flex items-center">
                      Tasks
                      <SortIcon column="tasks_enabled" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-white font-semibold cursor-pointer hover:bg-slate-700 transition-colors"
                    onClick={() => handleSort('email_enabled')}
                  >
                    <div className="flex items-center">
                      Email
                      <SortIcon column="email_enabled" />
                    </div>
                  </TableHead>
                  <TableHead className="text-white font-semibold">Reset Password</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {renderUserRows()}
              </TableBody>
            </Table>
          )}
        </CardContent>
        )}
      </Card>

      {/* User Audit Panel */}
      <Card className="border-slate-200">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={toggleAuditSection}
        >
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-serif">User Audit Log</CardTitle>
              <CardDescription>Login activity tracking (excludes superadmin) • {auditLogs.length} total entries</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {currentUser?.role === 'superadmin' && auditLogs.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  onClick={(e) => { e.stopPropagation(); handleClearAuditLogs(); }}
                  disabled={formLoading}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
              <Button variant="ghost" size="icon">
                {auditCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        {!auditCollapsed && (
          <CardContent>
            {auditLogs.length === 0 ? (
              <div className="text-center py-12">
                <Activity className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="font-serif text-lg font-semibold text-slate-700">No audit logs yet</h3>
                <p className="text-slate-500 mt-1">Login activity will appear here</p>
              </div>
            ) : (
              <>
                {/* Display limit buttons */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-slate-500">Show:</span>
                  <div className="flex gap-1">
                    <Button 
                      variant={auditDisplayLimit === 25 ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setAuditDisplayLimit(25)}
                      className={auditDisplayLimit === 25 ? "bg-slate-800" : ""}
                    >
                      25
                    </Button>
                    <Button 
                      variant={auditDisplayLimit === 100 ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setAuditDisplayLimit(100)}
                      className={auditDisplayLimit === 100 ? "bg-slate-800" : ""}
                    >
                      100
                    </Button>
                    <Button 
                      variant={auditDisplayLimit === 'all' ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setAuditDisplayLimit('all')}
                      className={auditDisplayLimit === 'all' ? "bg-slate-800" : ""}
                    >
                      All ({auditLogs.length})
                    </Button>
                  </div>
                  <span className="text-sm text-slate-400 ml-2">
                    Showing {displayedAuditLogs.length} of {auditLogs.length}
                  </span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-800 text-white">
                      <TableHead className="text-white font-semibold">User</TableHead>
                      <TableHead className="text-white font-semibold">Role</TableHead>
                      <TableHead className="text-white font-semibold">IP Address</TableHead>
                      <TableHead className="text-white font-semibold">Browser / OS</TableHead>
                      <TableHead className="text-white font-semibold">Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedAuditLogs.map((log, index) => {
                      const { browser, os } = parseUserAgent(log.user_agent);
                      return (
                      <TableRow key={index} className="hover:bg-slate-50">
                        <TableCell>
                          <div>
                            <div className="font-medium">{log.name}</div>
                            <div className="text-sm text-slate-500">{log.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={
                              log.role === 'manager' 
                                ? 'bg-blue-100 text-blue-800' 
                                : log.role === 'professional'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-slate-100 text-slate-600'
                            }
                          >
                            {log.role === 'viewer' ? 'Partner' : log.role.charAt(0).toUpperCase() + log.role.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm">
                            <Globe className="w-4 h-4 mr-2 text-slate-400" />
                            {log.ip_address}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm">
                            <Monitor className="w-4 h-4 mr-2 text-slate-400" />
                            {browser} / {os}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm">
                            <Clock className="w-4 h-4 mr-2 text-slate-400" />
                            {new Date(log.timestamp).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </>
            )}
          </CardContent>
        )}
      </Card>

      {/* Tasks Overview Panel - Only visible to SuperAdmin and Manager */}
      {(currentUser?.role === 'superadmin' || currentUser?.role === 'manager') && (
        <Card className="border-slate-200">
          <CardHeader 
            className="cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={toggleTasksSection}
          >
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-serif flex items-center gap-2">
                  <ListTodo className="w-5 h-5" />
                  All Tasks Overview
                </CardTitle>
                <CardDescription>Tasks from all users • {allTasks.length} total task(s)</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Link to="/tasks">
                  <Button variant="outline" size="sm" onClick={(e) => e.stopPropagation()}>
                    Open Tasks Page
                  </Button>
                </Link>
                <Button variant="ghost" size="icon">
                  {tasksCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
                </Button>
              </div>
            </div>
          </CardHeader>
          {!tasksCollapsed && (
            <CardContent>
              {allTasks.length === 0 ? (
                <div className="text-center py-12">
                  <ListTodo className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="font-serif text-lg font-semibold text-slate-700">No tasks yet</h3>
                  <p className="text-slate-500 mt-1">Tasks created by users will appear here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="w-[80px] cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleTaskSort('priority')}
                      >
                        <div className="flex items-center">
                          Priority
                          <TaskSortIcon column="priority" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleTaskSort('llc_name')}
                      >
                        <div className="flex items-center">
                          LLC
                          <TaskSortIcon column="llc_name" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleTaskSort('assigned_to_name')}
                      >
                        <div className="flex items-center">
                          Assigned To
                          <TaskSortIcon column="assigned_to_name" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleTaskSort('created_by_name')}
                      >
                        <div className="flex items-center">
                          Assigned By
                          <TaskSortIcon column="created_by_name" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="w-[200px] cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleTaskSort('task_description')}
                      >
                        <div className="flex items-center">
                          Task
                          <TaskSortIcon column="task_description" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleTaskSort('created_at')}
                      >
                        <div className="flex items-center">
                          Created
                          <TaskSortIcon column="created_at" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleTaskSort('due_date')}
                      >
                        <div className="flex items-center">
                          Due Date
                          <TaskSortIcon column="due_date" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleTaskSort('status')}
                      >
                        <div className="flex items-center">
                          Status
                          <TaskSortIcon column="status" />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedTasks.slice(0, 20).map((task) => {
                      const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.Low;
                      const PriorityIcon = priorityConfig.icon;
                      const isOverdue = task.due_date && new Date(task.due_date) < new Date() && !task.date_completed;
                      
                      return (
                        <TableRow key={task.id} className={task.date_completed ? 'opacity-60' : ''}>
                          <TableCell>
                            <Badge variant="outline" className={`${priorityConfig.color} text-xs`}>
                              <PriorityIcon className="w-3 h-3 mr-1" />
                              {task.priority}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Link 
                              to={`/llc/${task.llc_id}`}
                              className="text-amber-700 hover:text-amber-900 hover:underline font-medium text-sm"
                            >
                              {task.llc_name}
                            </Link>
                          </TableCell>
                          <TableCell className="text-sm">{task.assigned_to_name}</TableCell>
                          <TableCell className="text-sm text-slate-600">{task.created_by_name}</TableCell>
                          <TableCell className={`text-sm whitespace-normal max-w-[200px] ${task.date_completed ? 'line-through text-slate-400' : ''}`}>
                            {task.task_description}
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {formatDate(task.created_at)}
                          </TableCell>
                          <TableCell className={`text-sm ${isOverdue ? 'text-rose-600 font-semibold' : ''}`}>
                            {formatDate(task.due_date)}
                          </TableCell>
                          <TableCell>
                            {task.date_completed ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Done
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-600">
                                <Circle className="w-3 h-3 mr-1" />
                                Open
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              {allTasks.length > 20 && (
                <div className="text-center mt-4">
                  <Link to="/tasks">
                    <Button variant="outline" size="sm">
                      View all {allTasks.length} tasks
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Sync Dialog */}
      <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add Partners as Users</DialogTitle>
            <DialogDescription>Create user accounts for partners without login access.</DialogDescription>
          </DialogHeader>
          {!syncResults ? (
            <>
              <div className="py-4">
                <p className="text-sm text-slate-600 mb-3">This will sync partner data to user accounts:</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {renderInvestorItems()}
                </div>
                <p className="text-xs text-slate-500 mt-3">New accounts get default passwords. Existing accounts get contact info updates.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSyncDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSyncInvestors} className="bg-amber-700 hover:bg-amber-800" disabled={formLoading}>
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sync Partners'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="py-4">
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg mb-4">
                  <p className="text-emerald-800 font-medium">{syncResults.message}</p>
                </div>
                {syncResults.created && syncResults.created.length > 0 && (
                  <div className="space-y-2 mb-3">
                    <p className="text-sm font-medium text-slate-700">Created accounts:</p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {renderCreatedItems()}
                    </div>
                  </div>
                )}
                {syncResults.linked && syncResults.linked.length > 0 && (
                  <div className="space-y-1 mb-3">
                    <p className="text-sm font-medium text-slate-700">Linked existing accounts:</p>
                    <ul className="text-xs text-slate-600 list-disc list-inside">
                      {syncResults.linked.map((email, i) => <li key={i}>{email}</li>)}
                    </ul>
                  </div>
                )}
                {syncResults.updated && syncResults.updated.length > 0 && (
                  <div className="space-y-1 mb-3">
                    <p className="text-sm font-medium text-slate-700">Updated contacts:</p>
                    <ul className="text-xs text-slate-600 list-disc list-inside">
                      {syncResults.updated.map((email, i) => <li key={i}>{email}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={() => { setSyncDialogOpen(false); setSyncResults(null); }}>Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={(open) => { setResetDialogOpen(open); if (!open) { setSelectedUser(null); setNewPassword(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {selectedUser?.name}</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 6 characters"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetDialogOpen(false); setSelectedUser(null); setNewPassword(''); }}>Cancel</Button>
            <Button onClick={handleResetPassword} className="bg-slate-900 hover:bg-slate-800" disabled={formLoading || newPassword.length < 6}>
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedUser(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Emulate User Dialog */}
      <AlertDialog open={emulateDialogOpen} onOpenChange={setEmulateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Emulate User</AlertDialogTitle>
            <AlertDialogDescription>
              You will be logged in as <strong>{selectedUser?.name}</strong> ({selectedUser?.email}). 
              You can return to your admin account by logging out and logging back in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedUser(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleEmulateUser} 
              className="bg-purple-600 hover:bg-purple-700"
              disabled={formLoading}
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              View as {selectedUser?.name}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Users;
